import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "@/components/app/Bits";
import {
  Check, ShieldCheck, Sparkles, PawPrint, Mail, Users, Lock,
  HelpCircle, Receipt,
} from "lucide-react";
import { Logo } from "@/components/app/Logo";
import BrandHero from "@/components/app/BrandHero";
import { format, parseISO } from "date-fns";

const FEATURE_MATRIX = [
  { label: "Active dogs", free: "1 dog", pro: "Unlimited dogs", icon: PawPrint },
  { label: "Daily / weekly prompt tracker", free: "Yes", pro: "Yes", icon: Sparkles },
  { label: "Health & Vet records", free: "Yes", pro: "Yes", icon: ShieldCheck },
  { label: "Calendar & journal", free: "Yes", pro: "Yes", icon: Check },
  { label: "Show Plans, Show Results/Titles, Assessment, Goals", free: "Yes", pro: "Yes", icon: Check },
  { label: "Leading Edge Academy ebooks", free: "Yes", pro: "Yes", icon: Check },
  { label: "Newsletters", free: "Yes", pro: "Yes", icon: Mail },
  { label: "Email reminders delivered to your inbox", free: "—", pro: "Daily / Weekly / One-time", icon: Mail },
  { label: "Multi-user sharing (view & edit)", free: "—", pro: "Share with co-owners or handlers", icon: Users },
  { label: "Live Webinars", free: "—", pro: "Members-only sessions", icon: Sparkles },
  { label: "Yearly Conference", free: "—", pro: "Every January", icon: Sparkles },
  { label: "Future Pro features as we add them", free: "—", pro: "Always included", icon: Sparkles },
];

const FAQ = [
  {
    q: "Is there a free tier?",
    a: "Yes — Free gives you one dog and full read/write access to most sections. Email reminders and multi-user sharing are reserved for Pro.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Pro is billed upfront for the period you choose (monthly or yearly). If you stop subscribing, your account simply reverts to Free at the end of the period you already paid for — no surprise renewals.",
  },
  {
    q: "What payment methods are accepted?",
    a: "All major credit / debit cards (Visa, Mastercard, Amex, Discover) and Apple Pay / Google Pay where supported. Payments are processed securely by Stripe — we never see your card details.",
  },
  {
    q: "Do you store my payment information?",
    a: "No. Card data lives only with Stripe. We only store the subscription status (active or not) and the expiry date on your account.",
  },
  {
    q: "What happens to my data if my Pro subscription ends?",
    a: "Nothing is deleted. All your dogs, calendar entries, photos and notes remain in place — Pro-only features (email sending, sharing, extra dogs beyond the first) simply pause until you upgrade again.",
  },
  {
    q: "Can I upgrade from monthly to yearly?",
    a: "Yes — purchasing yearly while you're on monthly extends your access by a year. Time stacks; nothing is lost.",
  },
];

export default function SubscriptionPage() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription/me");
      setSub(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/checkout/subscription", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBusy(null);
    }
  };

  const active = sub?.active;

  return (
    <div className="space-y-12" data-testid="subscription-page">
      <BrandHero
        overline="Subscription"
        title="Pricing & what you get"
        description={(<>We make achieving your goals easy with <strong>products, services, and guidance you can trust</strong>.</>)}
        testid="subscription-brand-hero"
      />

      {/* Status banner */}
      {!loading && (
        <Card className={`shadow-none rounded-lg ${sub?.free_pass_active || active || sub?.is_admin || sub?.trial_active ? "bg-secondary border-border" : "bg-accent border-border"}`} data-testid="subscription-status-banner">
          <CardContent className="p-5 sm:p-6 flex items-start gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              {sub?.free_pass_active ? <Sparkles size={20} /> : active || sub?.is_admin ? <ShieldCheck size={20} /> : <Sparkles size={20} />}
            </div>
            <div className="flex-1 min-w-0">
              {sub?.free_pass_active ? (
                <>
                  <p className="font-medium" data-testid="free-pass-banner-title">
                    Everything is free until {sub.free_pass_until ? format(parseISO(sub.free_pass_until), "MMMM d, yyyy") : "January 2027"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    During our launch window every feature — multi-dog dashboards, email reminders, sharing, weekly digests — is unlocked for every account. <strong>No credit card required.</strong> Plans below will start billing after the launch window ends.
                  </p>
                </>
              ) : active ? (
                <>
                  <p className="font-medium">You&apos;re on Pro · {sub.plan === "yearly" ? "Yearly" : "Monthly"}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Active until {sub.expires_at ? format(parseISO(sub.expires_at), "MMM d, yyyy") : "—"}. Manage or extend below.
                  </p>
                </>
              ) : sub?.is_admin ? (
                <>
                  <p className="font-medium" data-testid="admin-access-title">Admin · Full access (no expiry)</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    All Pro features are unlocked permanently for your admin account. Plans below are what your users see.
                  </p>
                </>
              ) : sub?.trial_active ? (
                <>
                  <p className="font-medium" data-testid="trial-status-title">
                    You&apos;re on a free Pro trial — {sub.trial_days_left} {sub.trial_days_left === 1 ? "day" : "days"} left
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    All features unlocked through {sub.trial_expires_at ? format(parseISO(sub.trial_expires_at), "MMM d, yyyy") : "—"}. <strong>No credit card required.</strong> Pick a plan below any time to keep your access going.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">You&apos;re on the Free plan</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Upgrade any time. Cancel any time. Your data stays where it is, no matter what.
                  </p>
                </>
              )}
            </div>
            <Link
              to="/settings"
              className="text-sm font-medium underline underline-offset-4 self-center"
              data-testid="manage-from-settings-link"
            >
              Manage in Settings →
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Pricing cards */}
      <div>
        <h3 className="font-display text-2xl font-extrabold tracking-tight mb-1">Pick a plan</h3>
        <p className="text-sm text-muted-foreground mb-6">No tiers, no add-ons — one Pro plan, two ways to pay.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 items-stretch">
          {/* FREE */}
          <Card className="border-border shadow-none rounded-lg" data-testid="plan-explain-free">
            <CardContent className="p-6 sm:p-8 space-y-5 flex flex-col h-full">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Free</p>
                <p className="font-display text-5xl font-extrabold tracking-tighter mt-2">
                  $0<span className="text-base font-medium text-muted-foreground ml-1">/ forever</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Great for solo pet parents tracking one dog.</p>
              </div>
              <ul className="space-y-2 text-sm flex-1">
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> 1 active dog</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Full calendar, journal, Health &amp; Vet</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Show Plans &amp; Show Results/Titles</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Newsletters</li>
                <li className="flex items-start gap-2"><Lock size={14} className="text-muted-foreground mt-0.5 shrink-0" /> Email reminders &mdash; Pro only</li>
                <li className="flex items-start gap-2"><Lock size={14} className="text-muted-foreground mt-0.5 shrink-0" /> Multi-user sharing &mdash; Pro only</li>
                <li className="flex items-start gap-2"><Lock size={14} className="text-muted-foreground mt-0.5 shrink-0" /> Live Webinars &mdash; Pro only</li>
                <li className="flex items-start gap-2"><Lock size={14} className="text-muted-foreground mt-0.5 shrink-0" /> Yearly Conference &mdash; Pro only</li>
              </ul>
              <Button variant="outline" disabled className="w-full">Current plan</Button>
            </CardContent>
          </Card>

          {/* MONTHLY */}
          <Card className="border-border shadow-none rounded-lg relative" data-testid="plan-explain-monthly">
            <CardContent className="p-6 sm:p-8 space-y-5 flex flex-col h-full">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pro · Monthly</p>
                <p className="font-display text-5xl font-extrabold tracking-tighter mt-2">
                  $9<span className="text-base font-medium text-muted-foreground ml-1">/ month</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Lowest commitment. Pause whenever life gets busy.</p>
              </div>
              <ul className="space-y-2 text-sm flex-1">
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Unlimited dogs</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Newsletters</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Email reminders (Daily / Weekly / One-time)</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Multi-user sharing</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Live Webinars</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Yearly Conference (January)</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Every future Pro feature</li>
                <li className="flex items-start gap-2 pt-2 mt-2 border-t border-border">
                  <Users size={14} className="text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground/80"><strong>Breeders, trainers, clubs:</strong> <a href="mailto:hello@yourdogdays.com?subject=Group%20pricing%20enquiry" className="underline underline-offset-4">Contact for special pricing</a>.</span>
                </li>
              </ul>
              <Button
                onClick={() => subscribe("monthly")}
                disabled={!!busy || loading || sub?.free_pass_active}
                data-testid="explain-subscribe-monthly"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {sub?.free_pass_active
                  ? "Free until Jan 2027"
                  : busy === "monthly" ? "Redirecting…" : active ? "Extend monthly" : "Subscribe — $9/mo"}
              </Button>
            </CardContent>
          </Card>

          {/* YEARLY */}
          <Card className="border-primary border-2 shadow-sm rounded-lg relative" data-testid="plan-explain-yearly">
            <span className="absolute -top-3 right-5 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded">
              Save 16%
            </span>
            <CardContent className="p-6 sm:p-8 space-y-5 flex flex-col h-full">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pro · Yearly</p>
                <p className="font-display text-5xl font-extrabold tracking-tighter mt-2">
                  $90<span className="text-base font-medium text-muted-foreground ml-1">/ year</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  ≈ $7.50/mo &mdash; two months on the house.
                </p>
              </div>
              <ul className="space-y-2 text-sm flex-1">
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Everything in monthly</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> 1 year of access at a flat rate</li>
                <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> Easiest plan for show-season prep</li>
                <li className="flex items-start gap-2 pt-2 mt-2 border-t border-border">
                  <Users size={14} className="text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground/80"><strong>Breeders, trainers, clubs:</strong> <a href="mailto:hello@yourdogdays.com?subject=Group%20pricing%20enquiry" className="underline underline-offset-4">Contact for special pricing</a>.</span>
                </li>
              </ul>
              <Button
                onClick={() => subscribe("yearly")}
                disabled={!!busy || loading || sub?.free_pass_active}
                data-testid="explain-subscribe-yearly"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {sub?.free_pass_active
                  ? "Free until Jan 2027"
                  : busy === "yearly" ? "Redirecting…" : active ? "Extend yearly" : "Subscribe — $90/yr"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feature comparison */}
      <div>
        <h3 className="font-display text-2xl font-extrabold tracking-tight mb-1">What you get</h3>
        <p className="text-sm text-muted-foreground mb-6">Side-by-side feature comparison.</p>
        <Card className="border-border shadow-none rounded-lg overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-[1.6fr_1fr_1.4fr] sm:grid-cols-[2fr_1fr_1.4fr] border-b border-border bg-muted/40">
              <div className="px-5 py-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">Feature</div>
              <div className="px-5 py-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">Free</div>
              <div className="px-5 py-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">Pro</div>
            </div>
            {FEATURE_MATRIX.map((row, i) => {
              const Icon = row.icon;
              return (
                <div
                  key={row.label}
                  className={`grid grid-cols-[1.6fr_1fr_1.4fr] sm:grid-cols-[2fr_1fr_1.4fr] items-start text-sm ${
                    i < FEATURE_MATRIX.length - 1 ? "border-b border-border" : ""
                  }`}
                  data-testid={`feature-row-${i}`}
                >
                  <div className="px-5 py-4 flex items-start gap-2.5">
                    <Icon size={15} className="text-primary mt-0.5 shrink-0" />
                    <span>{row.label}</span>
                  </div>
                  <div className={`px-5 py-4 ${row.free === "—" ? "text-muted-foreground" : ""}`}>{row.free}</div>
                  <div className="px-5 py-4 font-medium">{row.pro}</div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Billing details */}
      <div>
        <h3 className="font-display text-2xl font-extrabold tracking-tight mb-1">Billing &amp; security</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mt-6">
          {[
            { icon: ShieldCheck, title: "Secure by Stripe", body: "Card data never touches our servers. Payments go directly to Stripe's PCI-compliant infrastructure." },
            { icon: Receipt, title: "Honest billing", body: "You pay only for the period you pick. No silent renewals — extend when you're ready." },
            { icon: Lock, title: "Your data stays put", body: "Cancel and your dogs, journals, photos and notes stay exactly where they are. Pro features simply pause." },
          ].map((c) => (
            <Card key={c.title} className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 sm:p-6">
                <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-3">
                  <c.icon size={18} />
                </div>
                <p className="font-medium">{c.title}</p>
                <p className="text-sm text-muted-foreground mt-1.5">{c.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div>
        <h3 className="font-display text-2xl font-extrabold tracking-tight mb-1">Frequently asked</h3>
        <p className="text-sm text-muted-foreground mb-6">Quick answers to common questions.</p>
        <div className="space-y-3" data-testid="subscription-faq">
          {FAQ.map((f, i) => (
            <details
              key={f.q}
              className="group border border-border rounded-md bg-card open:bg-secondary transition-colors"
              data-testid={`faq-item-${i}`}
            >
              <summary className="cursor-pointer px-5 py-4 flex items-center justify-between gap-3 list-none">
                <span className="flex items-center gap-2.5 font-medium text-sm">
                  <HelpCircle size={15} className="text-primary shrink-0" />
                  {f.q}
                </span>
                <span className="text-muted-foreground transition-transform group-open:rotate-45 text-lg leading-none">+</span>
              </summary>
              <div className="px-5 pb-4 pl-12 text-sm text-muted-foreground">{f.a}</div>
            </details>
          ))}
        </div>
      </div>

      {/* Group / Breeder callout */}
      <Card className="border-2 border-primary shadow-none rounded-lg bg-card" data-testid="group-pricing-callout">
        <CardContent className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex gap-4">
            <div className="h-12 w-12 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Users size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Clubs &amp; academies</p>
              <h4 className="font-display text-lg font-bold tracking-tight mt-1">Run a dog club or training academy?</h4>
              <p className="text-sm text-muted-foreground mt-1.5">
                Contact us for special group pricing.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="h-12 w-12 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <PawPrint size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Breeders</p>
              <h4 className="font-display text-lg font-bold tracking-tight mt-1">Same for dog breeders</h4>
              <p className="text-sm text-muted-foreground mt-1.5">
                Seamlessly transfer puppy profiles to new owners!
              </p>
            </div>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3 items-center justify-between border-t border-border pt-5">
            <p className="text-sm text-foreground/80">
              Reach out and tell us about your group — we&apos;ll put together a plan that fits.
            </p>
            <a href="mailto:hello@yourdogdays.com?subject=Group%20pricing%20enquiry" data-testid="group-pricing-contact">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Mail size={14} className="mr-1.5" /> Contact us
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Footer CTA */}
      {!active && (
        <Card className="border-border shadow-none rounded-lg bg-primary text-primary-foreground" data-testid="subscription-footer-cta">
          <CardContent className="p-8 sm:p-10 flex items-center justify-between gap-6 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] opacity-70">Ready when you are</p>
              <h4 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter mt-1">Try Pro &mdash; cancel any time</h4>
              <p className="text-sm opacity-80 mt-1.5 max-w-md">
                A month is $9. A year is $90. Whichever you pick, you can step back to Free without losing anything.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => subscribe("monthly")}
                disabled={!!busy || loading || sub?.free_pass_active}
                data-testid="footer-cta-monthly"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              >
                {sub?.free_pass_active ? "Free now" : "$9/mo"}
              </Button>
              <Button
                onClick={() => subscribe("yearly")}
                disabled={!!busy || loading || sub?.free_pass_active}
                data-testid="footer-cta-yearly"
                variant="outline"
                className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10"
              >
                {sub?.free_pass_active ? "Free now" : "$90/yr"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
